import { AdCard } from './AdCard'
import { useState } from 'react'
import { getCreativeFromPair } from 'app/util'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import BrokenImageIcon from '@mui/icons-material/BrokenImage'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { useHistory } from 'react-router-dom'
import { useGetBusinessID } from '@marketing-milk/frontend'
import { AdPair } from 'pages/business/campaigns/forms/CampaignForm'
import { FBAdPreviewFormat } from '@marketing-milk/interfaces'
import { getPreview } from 'pages/business/creatives/creative-wizard/ad-preview/views/getPreview'
import { Drawer, Button, Box } from '@mui/material'
import { useFetchCreativePreview } from 'hooks/creatives/fetch-creative-preview'

export interface AdSummaryProps {
  selectedCreatives?: AdPair[]
  sprintId?: number
  fromTab?: 'creatives' | 'draft-campaigns' | 'published-campaigns'
}

export function AdSummary({ selectedCreatives, sprintId, fromTab }: AdSummaryProps) {
  const [activeCreativesIndex, setActiveCreativesIndex] = useState(0)
  const [viewPreview, setViewPreview] = useState(false)
  const [previewCreativeId, setPreviewCreativeId] = useState<number | undefined>()
  const history = useHistory()
  const businessID = useGetBusinessID()
  const creativeA = getCreativeFromPair('A', activeCreativesIndex, selectedCreatives)
  const creativeB = getCreativeFromPair('B', activeCreativesIndex, selectedCreatives)
  const currentAdPair = activeCreativesIndex + 1
  const previousAdPair = () => setActiveCreativesIndex(activeCreativesIndex - 1)
  const nextAdPair = () => setActiveCreativesIndex(activeCreativesIndex + 1)

  const { adCreativePreview, retrievePreview } = useFetchCreativePreview(previewCreativeId)

  const handleEditCreative = (creative: ReturnType<typeof getCreativeFromPair>) => {
    if (creative?.id) {
      const baseRoute = sprintId ? 'sprints' : 'creatives'
      const params = new URLSearchParams()
      if (sprintId) params.append('sprintId', sprintId.toString())
      if (fromTab) params.append('tab', fromTab)
      const queryString = params.toString() ? `?${params.toString()}` : ''
      history.push(`/${businessID}/${baseRoute}/${creative.id}${queryString}`)
    }
  }

  const handlePreviewCreative = (creative: ReturnType<typeof getCreativeFromPair>) => {
    if (creative?.id) {
      setPreviewCreativeId(creative.id)
      setViewPreview(true)
      retrievePreview(creative.id)
    }
  }

  const getCardActions = (creative: ReturnType<typeof getCreativeFromPair>) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<VisibilityIcon />}
        onClick={() => handlePreviewCreative(creative)}
        size="small"
      >
        Preview
      </Button>
      <Button
        variant="contained"
        color="primary"
        startIcon={<EditIcon />}
        onClick={() => handleEditCreative(creative)}
        size="small"
      >
        Edit
      </Button>
    </Box>
  )

  const noCreative = (
    <div className="h-100 d-flex justify-content-center">
      <div className="center-vertical text-center">
        <BrokenImageIcon
          color="disabled"
          className="mx-auto mb-2"
          fontSize="large"
          style={{ fontSize: '40px' }}
        />
        <Typography variant="h5" className="MuiTypography-colorTextSecondary">
          No Creative Selected
        </Typography>
      </div>
    </div>
  )

  return (
    <div data-testid="ad-summary" className="w-100">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          {creativeA ? (
            <AdCard title="Creative A" creative={creativeA} actions={getCardActions(creativeA)} />
          ) : (
            noCreative
          )}
        </div>
        <div>
          {creativeB ? (
            <AdCard title="Creative B" creative={creativeB} actions={getCardActions(creativeB)} />
          ) : (
            noCreative
          )}
        </div>
      </div>
      {selectedCreatives && selectedCreatives?.length > 1 && (
        <div data-testid="ad-pair-controls" className="w-100 mt-4">
          <div className="d-flex fit-content mx-auto text-center">
            <IconButton
              data-testid="previous-ad-pair"
              size="medium"
              disabled={activeCreativesIndex === 0}
              onClick={previousAdPair}
            >
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
            <Typography className="mx-4 center-vertical" variant="subtitle1">
              {currentAdPair} of {selectedCreatives.length}
            </Typography>
            <IconButton
              data-testid="next-ad-pair"
              size="medium"
              disabled={currentAdPair === selectedCreatives.length}
              onClick={nextAdPair}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}

      <Drawer
        open={viewPreview}
        onClose={() => setViewPreview(false)}
        anchor="right"
        PaperProps={{
          style: { width: '500px' },
        }}
        ModalProps={{
          BackdropProps: {
            style: { backgroundColor: 'rgba(0, 0, 0, 0.2)' },
          },
        }}
      >
        <div className="flex flex-col p-4 h-full overflow-y-auto space-y-4">
          <div className="flex justify-between items-center border-b pb-3">
            <p className="font-extrabold text-xl">Ad Preview</p>
            <i
              className="fa fa-times cursor-pointer text-gray-600 hover:text-gray-900"
              onClick={() => setViewPreview(false)}
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {adCreativePreview && getPreview(FBAdPreviewFormat.MOBILE_FULLWIDTH, adCreativePreview)}
          </div>
        </div>
      </Drawer>
    </div>
  )
}
